import { ReactElement, useState } from "react";
import classNames from "classnames";
import { useUiContext } from "@rotorstudios/fe-ui";

import {
  NAVBAR_ALL_VEHICLES,
  NAVBAR_CUSTOMISE,
  NAVBAR_GALLERY,
  NAVBAR_NEXT_STEPS,
  NAVBAR_SAVE_AND_SHARE,
  NAVBAR_VIEW_IN_AR,
  QA_NAVBAR,
  QA_NAVBAR_BUTTON_ALL_VEHICLES,
  QA_NAVBAR_BUTTON_AR,
  QA_NAVBAR_LINK_AR,
  QA_NAVBAR_BUTTON_DROPDOWN,
  QA_NAVBAR_BUTTON_HOME,
  QA_NAVBAR_LINK_BUTTON,
  QA_NAVBAR_LINK,
  FEATURE_AR,
} from "../../modules/Data/static";
import UimLink from "../../uiComponents/molecules/uimLink/UimLink";
import UiMButtonAR, { iARButton } from "../uiMButtonAR/UiMButtonAR";
import useUiMMenuSticky from "./useUiMMenuSticky";
import { generateUniqueId } from "../../helpers/utils";
import TrackedButton from "../../modules/toyota-kit/TrackedButton";
import {
  GA_TOYOTA_FOR_YOU_CORE_PAGE,
  GaButtonName,
  GaEvent,
  GaSectionName,
} from "../../modules/fe-modules/GA/static";
import useKillSwitch from "../../modules/Data/useKillSwitch";
import { PAGE_HOME, PAGE_NEXT_STEPS } from "../../modules/fe-modules/Router/static";
import { useDataContext } from "../../modules/Data/DataProvider";
import "./_UiMMenuSticky.scss";

export type UiMMenuStickyProps = {
  ar?: iARButton;
  navbarPage?: "home" | "next-steps";
};

const UiMMenuSticky = ({ ar, navbarPage }: UiMMenuStickyProps): ReactElement => {
  const { breakpointActive, projectClass, isMobile } = useUiContext();
  const {
    handleAllVehicles,
    handleCustomise,
    handleGallery,
    handleSaveAndShare,
    handleNextSteps,
    handleBackToHome,
  } = useUiMMenuSticky();
  const { cmeSiloUiData } = useDataContext();
  const { isFeatureDisabled } = useKillSwitch();

  const [isOpen, setIsOpen] = useState(false);

  const getClassName = () =>
    classNames(`${projectClass}_UiMMenuSticky`, breakpointActive);

  const getDropdownArrow = () => (
    <button
      onClick={() => setIsOpen(!isOpen)}
      className={`${projectClass}_UiMMenuSticky_NavLinks_DropdownBtn`}
      data-qa={QA_NAVBAR_BUTTON_DROPDOWN}
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        className={`${projectClass}_UiMMenuSticky_NavLinks_DropdownBtn_Icon ${
          isOpen ? `${projectClass}_UiMMenuSticky_NavLinks_DropdownBtn_Icon--open` : ""
        }`}
      >
        <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" />
      </svg>
    </button>
  );

  const formatVehicleName = () => {
    const vehicleName = cmeSiloUiData?.model?.title || null;
    const shortVehicleName = cmeSiloUiData?.model?.shortTitle || null;
    return isMobile && shortVehicleName ? shortVehicleName : vehicleName;
  };

  const handleClick = (handler: () => void) => () => {
    setIsOpen(false);
    handler();
  };

  const isHome = navbarPage === PAGE_HOME;
  const isNextSteps = navbarPage === PAGE_NEXT_STEPS;

  const links = [
    {
      label: NAVBAR_CUSTOMISE,
      onClick: handleClick(handleCustomise),
      gaDataButtonName: GaButtonName.NAVBAR_CUSTOMISE,
      shouldRender: !isMobile && (isHome || isNextSteps),
      shouldRenderInDropdown: true,
      qaId: QA_NAVBAR_LINK_BUTTON,
    },
    {
      label: NAVBAR_GALLERY,
      onClick: handleClick(handleGallery),
      gaDataButtonName: GaButtonName.NAVBAR_GALLERY,
      shouldRender: !isMobile || isNextSteps,
      shouldRenderInDropdown: true,
      qaId: QA_NAVBAR_LINK_BUTTON,
    },
    {
      label: NAVBAR_SAVE_AND_SHARE,
      onClick: handleClick(handleSaveAndShare),
      gaDataButtonName: GaButtonName.NAVBAR_SHARE,
      shouldRender: !isMobile || (isMobile && isNextSteps),
      shouldRenderInDropdown: isHome,
      qaId: QA_NAVBAR_LINK_BUTTON,
    },
    {
      label: NAVBAR_NEXT_STEPS,
      onClick: handleClick(handleNextSteps),
      gaDataButtonName: GaButtonName.NAVBAR_NEXT_STEPS,
      shouldRender: isHome,
      shouldRenderInDropdown: false,
      qaId: QA_NAVBAR_LINK_BUTTON,
    },
    {
      label: NAVBAR_VIEW_IN_AR,
      onClick: () => {}, //  The AR onClick is fired within its own component
      gaDataButtonName: GaButtonName.VIEW_IN_AR_STICKY_MENU,
      shouldRender: false,
      shouldRenderInDropdown: isNextSteps && ar?.tLabel,
      qaId: QA_NAVBAR_LINK_AR,
      arLinkProps: {
        gaProps: {
          eventId: GaEvent.VIEW_IN_AR,
          viewName: GA_TOYOTA_FOR_YOU_CORE_PAGE,
          viewSection: GaSectionName.STICKY_MENU,
        },
        ar,
      },
    },
    {
      label: NAVBAR_ALL_VEHICLES,
      onClick: handleAllVehicles,
      gaDataButtonName: GaButtonName.NAVBAR_ALL_VEHICLES,
      shouldRender: false,
      shouldRenderInDropdown: true,
      qaId: QA_NAVBAR_BUTTON_ALL_VEHICLES,
    },
  ];

  const getARButton = (ar: iARButton, qaId: string) => (
    <UiMButtonAR
      key={generateUniqueId()}
      secondary
      inverse
      gaDataButtonName={GaButtonName.VIEW_IN_AR_STICKY_MENU}
      gaProps={{
        eventId: GaEvent.VIEW_IN_AR,
        buttonName: GaButtonName.VIEW_IN_AR_STICKY_MENU,
        viewName: GA_TOYOTA_FOR_YOU_CORE_PAGE,
        viewSection: GaSectionName.STICKY_MENU,
      }}
      ar={ar}
      qaId={qaId}
    />
  );

  const getNavbarItems = () => {
    const defaultItems = links
      .filter((link) => link.shouldRender)
      .map(({ label, onClick, qaId, gaDataButtonName }) => (
        <TrackedButton
          key={generateUniqueId()}
          variant={label === NAVBAR_NEXT_STEPS ? "ghost-right" : "icon-right"}
          onClick={onClick}
          gaDataButtonName={gaDataButtonName}
          containerQaId={QA_NAVBAR_LINK}
          qaId={qaId}
        >
          {label}
        </TrackedButton>
      ));

    return isHome && ar?.tLabel && !isFeatureDisabled(FEATURE_AR)
      ? [...defaultItems, getARButton(ar, QA_NAVBAR_BUTTON_AR)]
      : defaultItems;
  };

  const getDropdownItems = () =>
    isOpen ? (
      <div className={`${projectClass}_UiMMenuSticky_NavLinks_Submenu`}>
        {links
          .filter((link) => isMobile && link.shouldRenderInDropdown)
          .map(({ label, onClick, gaDataButtonName, arLinkProps, qaId }) => (
            <UimLink
              className={
                isOpen ? `${projectClass}_UiMMenuSticky_NavLinks_Submenu_Item` : ""
              }
              key={generateUniqueId()}
              noIcon={label !== NAVBAR_NEXT_STEPS}
              tLabel={label}
              gaDataButtonName={gaDataButtonName}
              onClick={onClick}
              qaId={qaId}
              {...(label === NAVBAR_VIEW_IN_AR ? arLinkProps : {})}
            />
          ))}
      </div>
    ) : null;

  const getDropdownGroup = () => (
    <>
      <div className={`${projectClass}_UiMMenuSticky_Left_DropdownGroup`}>
        <TrackedButton
          variant="icon-right"
          onClick={handleBackToHome}
          gaDataButtonName={GaButtonName.NAVBAR_VEHICLE_HOME}
          qaId={QA_NAVBAR_BUTTON_HOME}
        >
          {formatVehicleName()}
        </TrackedButton>
        {isMobile && getDropdownArrow()}
      </div>
      {getDropdownItems()}
    </>
  );

  const getDesktopItems = () => (
    <>
      <div className={`${projectClass}_UiMMenuSticky_Left`}>
        {!isMobile && (
          <>
            <TrackedButton
              onClick={handleAllVehicles}
              qaId={QA_NAVBAR_BUTTON_ALL_VEHICLES}
              variant="icon-right"
            >
              {NAVBAR_ALL_VEHICLES}
            </TrackedButton>
            {isHome ? <span>{formatVehicleName()}</span> : getDropdownGroup()}
          </>
        )}
      </div>
      <div className={`${projectClass}_UiMMenuSticky_NavLinks ${breakpointActive}`}>
        {getNavbarItems()}
      </div>
    </>
  );

  const getNavbarContent = () => {
    if (isMobile) {
      return (
        <div className={`${projectClass}_UiMMenuSticky_NavLinks ${breakpointActive}`}>
          {getDropdownGroup()}
          {getNavbarItems()}
        </div>
      );
    }

    return getDesktopItems();
  };

  return (
    <nav className={getClassName()} data-qa={QA_NAVBAR}>
      {getNavbarContent()}
    </nav>
  );
};

export default UiMMenuSticky;
